import { Label } from '@radix-ui/react-label'
import { ReactNode, Ref } from 'react'

import { SizeOptions } from '../../../types/types'
import { AstraParagraph } from '../../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../../Utilities/astraTwMerge'
import { AstraErrorText } from './AstraErrorText'
import { AstraHelperText } from './AstraHelperText'

export type InputBase = {
    startIcon?: ReactNode
    endIcon?: ReactNode
    helperText?: string
    errorText?: string
    error?: boolean
    label?: string
    fullWidth?: boolean
    borderless?: boolean
}

export type AstraInputProps = {
    id?: string
    disabled?: boolean
    readOnly?: boolean
    size?: SizeOptions
    className?: string
    children?: ReactNode
    labelHtmlFor?: string
    onClick?: () => void
    ref?: Ref<HTMLDivElement>
} & InputBase

export const getHelperTextId = (id: string) => `helper-text-${id}`

export const inputTypographyStyles: Record<SizeOptions, string> = {
    small: 'text-body-small leading-body-small tracking-body-small',
    medium: 'text-body leading-body tracking-body',
    large: 'text-body leading-body tracking-body',
}

export const inputSizeStyles: Record<SizeOptions, string> = {
    small: 'min-h-5', // 20px, 1.25rem
    medium: 'min-h-7', // 28px, 1.75rem
    large: 'min-h-9', // 36px, 2.25rem
}

// The focus ring is forced over any hover border: Tailwind emits hover variants after focus-within,
// so at equal specificity a hovered input would otherwise keep its hover border while focused.
const inputBaseStyles =
    'overflow-hidden transition flex items-center gap-2 justify-between bg-surface-base outline-none focus-within:!border-ring px-2 rounded-md text-default group'

const borderStyles = 'border border-strong'

const disabledStyles = 'text-disabled bg-surface-strong border-disabled'

const readOnlyStyles = 'text-default bg-surface-strong border-base'

const errorStyles = 'border-critical-ring bg-critical-subtle-rest hover:border-critical-base'

export const AstraInputWrapper = ({
    id,
    label,
    helperText,
    errorText,
    error,
    disabled,
    readOnly,
    fullWidth,
    className,
    children,
    labelHtmlFor,
    onClick,
    ref,
    startIcon,
    endIcon,
    size = 'small',
    borderless = false,
}: AstraInputProps) => (
    <div className={astraTwMerge('relative flex flex-col gap-1', fullWidth ? 'w-full' : '')} ref={ref}>
        {label && (
            <Label htmlFor={labelHtmlFor}>
                <AstraParagraph variant="body-small" weight="medium" className="select-none">
                    {label}
                </AstraParagraph>
            </Label>
        )}
        <AstraHelperText helperText={helperText} id={id} />
        <div
            className={astraTwMerge(
                inputBaseStyles,
                inputSizeStyles[size],
                borderless ? '' : borderStyles,
                error ? errorStyles : '',
                disabled ? disabledStyles : '',
                readOnly ? readOnlyStyles : '',
                className
            )}
            aria-label={label}
            onClick={onClick}
        >
            {startIcon && <span className="group-[.is-expanded]:self-start group-[.is-expanded]:pt-[3px]">{startIcon}</span>}
            {children}
            {endIcon && <span>{endIcon}</span>}
        </div>
        <AstraErrorText errorText={errorText} error={error} />
    </div>
)
